import { createPortal } from 'react-dom';
import { Button } from './ui';
import './InstagramConnectionErrorModal.css';

type InstagramConnectionErrorModalProps = {
  authStatus?: string;
  onClose: () => void;
  onRetry?: () => void;
};

function getInstagramErrorGuidance(authStatus?: string): { title: string; steps: string[] } {
  switch ((authStatus || '').toLowerCase()) {
    case 'invalid_credentials':
      return {
        title: 'Login credentials rejected',
        steps: [
          '1. Check your phone for any Instagram prompts or notifications — if you see one, complete that action first.',
          '2. Still having trouble? Open the Instagram app and go to Settings → Profile Overview → Password and Security, then change your password.',
          '3. After changing it, try logging in again with the new password.',
          '4. If it still fails, try selecting a different country above and start over.',
        ],
      };
    case 'challenge_required':
      return {
        title: 'Instagram requires a security check',
        steps: [
          '1. Open Instagram on your phone right now — you should see a security challenge.',
          "2. Complete the challenge (confirm it's you, enter a code, etc).",
          '3. Once completed in the app, come back here and try connecting again.',
          "4. If that doesn't work, try selecting a different country and start over.",
        ],
      };
    case 'risk_blocked':
      return {
        title: 'Instagram needs a moment',
        steps: [
          '1. Wait a few hours before trying again.',
          '2. Use your account normally in the Instagram app during this time.',
          '3. Check your phone for any security notifications from Instagram and follow any prompts.',
          '4. After waiting, come back and try connecting again.',
        ],
      };
    case 'ip_block':
    case 'proxy_error':
      return {
        title: 'Connection temporarily unavailable',
        steps: [
          '1. Wait a few minutes and try again — this is usually temporary.',
          '2. If it keeps failing, try selecting a different account region above.',
          '3. Make sure you can still log in to Instagram normally on your phone.',
        ],
      };
    case 'session_expired':
      return {
        title: 'Session expired',
        steps: [
          '1. Your stored connection is no longer valid.',
          '2. Just reconnect with your username and password again.',
          '3. If that fails, check your phone for any Instagram prompts.',
          '4. Try selecting a different country and start over.',
        ],
      };
    default:
      return {
        title: 'Instagram connection failed',
        steps: [
          '1. Check your phone for any Instagram notifications or prompts — complete them if you see any.',
          '2. Try entering the newest authenticator code again.',
          '3. If it still fails, try selecting a different country above and start over.',
          '4. Wait a bit and retry — sometimes Instagram needs time between attempts.',
        ],
      };
  }
}

export function InstagramConnectionErrorModal({
  authStatus,
  onClose,
  onRetry,
}: InstagramConnectionErrorModalProps) {
  const guidance = getInstagramErrorGuidance(authStatus);

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal instagram-error-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🔐 {guidance.title}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close dialog">
            ×
          </button>
        </div>
        <div className="modal-body instagram-error-body">
          <div className="error-guidance">
            <p className="guidance-intro">Here's what to do:</p>
            <ol className="guidance-steps">
              {guidance.steps.map((step, idx) => (
                <li key={idx}>{step}</li>
              ))}
            </ol>
          </div>
        </div>
        <div className="modal-footer">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
          {onRetry && (
            <Button variant="primary" size="sm" onClick={onRetry}>
              Try Again
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
